import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getShippingRates, createCheckoutSession, getProducts } from '../services/api';
import SEO from '../components/SEO';
import { useLang } from '../i18n/LanguageContext';
import { useCart } from '../contexts/CartContext';
import GamifiedButton from '../components/common/GamifiedButton';
import GameProgress from '../components/common/GameProgress';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { PAYS_LIVRES, normaliserPays, estLivrable } from '../lib/zonesLivraison';

/**
 * Sendcloud décrit chaque méthode par `service_point_input` : 'required' pour un
 * retrait en point relais, 'none' pour une livraison à l'adresse du client.
 * On ne devine donc pas d'après le nom du transporteur.
 */
const exigePointRelais = (rate) => rate?.service_point_input === 'required';

/**
 * Nombre d'appels tentés pour obtenir les tarifs, reprises comprises.
 *
 * Le transporteur a des ratés passagers, mesurés en production le 14/08/2026 :
 * trois échecs sur une vingtaine d'appels, puis douze d'affilée sans incident.
 * Assez rare pour passer inaperçu, assez fréquent pour coûter des ventes — et
 * jusqu'ici chacun de ces ratés annonçait à l'acheteur qu'on ne livrait pas
 * chez lui. On réessaie donc tout seul avant de lui montrer quoi que ce soit.
 */
const ESSAIS_TARIFS = 3;

/** Attente avant la première reprise ; elle double à la suivante. */
const DELAI_REPRISE_MS = 1200;

/** Au-delà, on explique l'attente plutôt que de laisser l'écran figé. */
const DELAI_MESSAGE_ATTENTE_MS = 2500;

export default function Checkout() {
  const { t, lang } = useLang();
  const { user, loading } = useAuth();

  const location = useLocation();
  const { cart, discount: cartDiscount } = useCart();
  const [currentStep, setCurrentStep] = useState(1);
  const [products, setProducts] = useState([]);

  // Fetch products to ensure we have the latest weight
  useEffect(() => {
    getProducts().then(data => {
      if (Array.isArray(data)) setProducts(data);
    }).catch(err => console.error('Error fetching products for weight sync:', err));
  }, []);

  /**
   * Poids du panier, en kilogrammes.
   *
   * Le poids de référence est celui de la base, mais on n'a PAS besoin de
   * l'attendre : chaque article du panier porte déjà le sien. On calcule donc
   * tout de suite, quitte à affiner quand /api/products répond.
   *
   * C'est ce qui manquait. Le poids restait nul tant que l'appel produits
   * n'avait pas abouti, aucune demande de tarif ne partait, et l'étape 2
   * affichait « livraison indisponible vers ce pays » — sur un pays desservi,
   * sans avoir rien demandé à personne. Une saisie au clavier prend vingt
   * secondes et laissait à l'appel le temps d'arriver ; l'autofill du
   * navigateur remplit tout en une fraction de seconde et tombait donc en
   * plein dedans. Un appel produits en échec (Cloudflare, réseau) provoquait
   * le même message, définitivement.
   */
  const getCartWeight = () => {
    return cart.reduce((acc, item) => {
      const product = products.find(p => p.id === item.id);
      const poidsProduit = product ? parseFloat(product.weight) : NaN;
      const poidsArticle = Number.isFinite(poidsProduit)
        ? poidsProduit
        : parseFloat(item.weight) || 0.5;
      return acc + (poidsArticle * (item.quantity || 1));
    }, 0).toFixed(3);
  };

  // Pre-fill with user data from profile
  const [shippingAddress, setShippingAddress] = useState({
    name: user?.fullName || '',
    email: user?.email || '',
    address: user?.address || '',
    city: user?.city || '',
    postal_code: user?.postalCode || '',
    country: normaliserPays(user?.country),
    weight: null // calculé dès le premier rendu par l'effet ci-dessous
  });
  // `null` = on n'a encore rien demandé au transporteur. Un tableau vide veut
  // dire tout autre chose : « demandé, et il n'y a rien ». Les deux étaient
  // confondus, et l'écran annonçait donc « pays non desservi » avant même la
  // première requête.
  const [rates, setRates] = useState(null);
  const [selectedRate, setSelectedRate] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  // Vrai quand l'appel des tarifs a ÉCHOUÉ, ce qui n'est pas la même chose
  // qu'une liste vide. Les deux cas affichaient le même message — « livraison
  // temporairement indisponible vers ce pays » — si bien qu'une coupure réseau
  // ou une erreur serveur accusait le pays de l'acheteur.
  const [erreurTarifs, setErreurTarifs] = useState(false);
  // Incrémenté par le bouton « Réessayer » : sert uniquement à relancer l'effet.
  const [tentativeTarifs, setTentativeTarifs] = useState(0);
  // L'appel dépasse quelques secondes : on l'annonce au lieu de laisser un
  // écran figé, qui se lit comme une panne.
  const [attenteLongue, setAttenteLongue] = useState(false);
  // Numéro de la reprise automatique en cours, 0 quand il n'y en a pas.
  const [repriseEnCours, setRepriseEnCours] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [paymentSucceeded] = useState(false);

  // Update address when user data is loaded
  useEffect(() => {
    if (user) {
      setShippingAddress(prev => ({
        ...prev,
        name: prev.name || user.fullName || '',
        email: prev.email || user.email || '',
        address: prev.address || user.address || '',
        city: prev.city || user.city || '',
        postal_code: prev.postal_code || user.postalCode || '',
        country: prev.country || normaliserPays(user.country)
      }));
    }
  }, [user]);

  // Le poids est recalculé à chaque changement de panier, et une deuxième fois
  // quand les produits arrivent. Il n'attend plus /api/products : sans cela,
  // un appel lent ou en échec empêchait toute demande de tarif.
  useEffect(() => {
    if (cart.length > 0) {
      setShippingAddress(prev => ({
        ...prev,
        weight: getCartWeight()
      }));
    }
  }, [products, cart]);

  /**
   * Numéro de la dernière demande de tarifs émise.
   *
   * L'autofill du navigateur remplit les champs par rafales : nom, adresse,
   * ville, code postal et pays arrivent en quelques millisecondes, chacun dans
   * son propre événement. L'effet partait donc plusieurs fois de suite, avec
   * des adresses successivement incomplètes, et rien ne garantissait l'ordre
   * d'arrivée des réponses. Celle d'une adresse périmée — un code postal sans
   * son pays, par exemple — pouvait écraser la bonne et vider la liste : c'est
   * ce qui affichait « livraison indisponible » sur une adresse belge
   * parfaitement desservie, alors que la même saisie au clavier fonctionnait.
   */
  const demandeTarifs = useRef(0);

  /**
   * Les options réellement présentées, une seule par transporteur ET par mode
   * de livraison — la moins chère.
   *
   * Sendcloud en renvoie vingt-cinq vers la Belgique, dont quatre « UPS
   * Standard » au même prix qui ne diffèrent que par une option de signature
   * ou une livraison le samedi. Les afficher toutes, c'est demander à
   * l'acheteur d'arbitrer entre des lignes qu'il ne sait pas distinguer, juste
   * avant de payer. On garde donc la moins chère de chaque couple
   * (transporteur, mode), sans jamais masquer un mode de livraison ni un
   * transporteur.
   */
  const optionsLivraison = useMemo(() => {
    if (!Array.isArray(rates)) return [];

    const meilleures = new Map();
    for (const option of rates) {
      const cle = `${option.carrier}|${exigePointRelais(option) ? 'relais' : 'domicile'}`;
      const connue = meilleures.get(cle);
      if (!connue || Number(option.price) < Number(connue.price)) {
        meilleures.set(cle, option);
      }
    }

    return [...meilleures.values()].sort((a, b) => Number(a.price) - Number(b.price));
  }, [rates]);

  /**
   * Ce qu'on montre d'emblée : la moins chère en point relais et la moins
   * chère à domicile. Deux lignes, une par mode — de quoi choisir sans lire un
   * catalogue. Le reste est à un clic.
   */
  const optionsMisesEnAvant = useMemo(() => {
    const relais = optionsLivraison.find((o) => exigePointRelais(o));
    const domicile = optionsLivraison.find((o) => !exigePointRelais(o));
    return optionsLivraison.filter((o) => o === relais || o === domicile);
  }, [optionsLivraison]);

  const [toutesOptions, setToutesOptions] = useState(false);
  // L'option choisie reste visible même repliée : sans cela, sélectionner une
  // option puis replier la liste ferait disparaître son propre choix.
  const optionsVisibles = toutesOptions
    ? optionsLivraison
    : optionsLivraison.filter(
        (o) => optionsMisesEnAvant.includes(o) || o.id === selectedRate?.id
      );

  useEffect(() => {
    // On normalise AVANT d'interroger le transporteur : Sendcloud ne reconnaît
    // que le code ISO en majuscules, et répond « aucun transporteur » — sans
    // erreur — pour « be » ou « Belgique ».
    const pays = normaliserPays(shippingAddress.country);
    const codePostal = (shippingAddress.postal_code || '').trim();
    const ville = (shippingAddress.city || '').trim();

    // Un code postal trop court ne renvoie rien d'exploitable : inutile
    // d'inquiéter l'acheteur pendant qu'il tape encore.
    if (!pays || !shippingAddress.weight || codePostal.length < 4 || !ville) return;

    const jeton = demandeTarifs.current + 1;
    demandeTarifs.current = jeton;
    const estCourante = () => jeton === demandeTarifs.current;

    setLoadingRates(true);
    setErreurTarifs(false);
    setAttenteLongue(false);
    setRepriseEnCours(0);

    // Au bout de quelques secondes, on dit à l'acheteur ce qui se passe. Un
    // écran qui ne bouge plus se lit comme une panne, et il s'en va.
    const minuterieAttente = setTimeout(() => {
      if (estCourante()) setAttenteLongue(true);
    }, DELAI_MESSAGE_ATTENTE_MS);

    const pause = (ms) => new Promise((resolution) => setTimeout(resolution, ms));

    (async () => {
      for (let essai = 1; essai <= ESSAIS_TARIFS; essai++) {
        try {
          /* 'BE' : les colis partent de Freux, en Belgique. Ce paramètre valait
             'FR', ce qui laissait croire à une expédition depuis la France — le
             backend l'ignore de toute façon et impose la Belgique. */
          const data = await getShippingRates('BE', pays, shippingAddress.weight, codePostal, ville);
          if (!estCourante()) return; // réponse périmée

          const flattenedRates = Array.isArray(data) ? data : [data];
          setRates(flattenedRates);
          // Le moins cher est en tête (tri fait par le backend) : on le
          // présélectionne pour que l'acheteur n'ait rien à faire dans le cas
          // le plus fréquent, tout en gardant les autres visibles.
          setSelectedRate(flattenedRates.length > 0 ? flattenedRates[0] : null);
          // Un point relais appartient à un transporteur ET à un pays : celui
          // choisi avant un changement d'adresse n'a plus de sens.
          setShippingAddress(prev => ({
            ...prev,
            service_point_id: null,
            service_point_address: null,
          }));
          setLoadingRates(false);
          return;
        } catch (err) {
          if (!estCourante()) return;
          console.error(`Tarifs, essai ${essai}/${ESSAIS_TARIFS} :`, err);

          // Une adresse que le serveur refuse ne deviendra pas valable en
          // insistant : inutile de faire patienter pour rien.
          const inutileDeReessayer = String(err?.message || '').includes('INVALID_DESTINATION_COUNTRY');

          if (essai < ESSAIS_TARIFS && !inutileDeReessayer) {
            // On réessaie tout seul. Le transporteur a des ratés passagers —
            // mesurés en production : trois échecs sur une vingtaine d'appels,
            // puis douze d'affilée sans incident. Laisser l'acheteur devant un
            // message d'erreur, c'était perdre la vente sur un incident d'une
            // seconde. L'attente s'allonge à chaque essai pour laisser au
            // transporteur le temps de se remettre.
            setRepriseEnCours(essai);
            await pause(DELAI_REPRISE_MS * essai);
            if (!estCourante()) return;
          } else {
            setRates([]);
            setSelectedRate(null);
            setErreurTarifs(true);
            setLoadingRates(false);
            return;
          }
        }
      }
    })();

    return () => clearTimeout(minuterieAttente);
  }, [
    shippingAddress.country,
    shippingAddress.weight,
    shippingAddress.postal_code,
    shippingAddress.city,
    tentativeTarifs,
  ]);

  if (loading) return <div className="flex items-center justify-center min-h-screen text-lg text-text">{t('tunnel.checkout.chargement')}</div>;

  // Plus de mur de compte : on renvoyait ici vers /login, ce qui obligeait à
  // s'inscrire pour un achat unique de 16 €. C'est le premier frein à l'achat
  // sur une boutique qu'on ne connaît pas. La commande invitée est identifiée
  // par l'e-mail saisi plus bas, que le backend enregistre en guest_email.

  // Redirect if cart is empty
  if (!cart || cart.length === 0) {
    return <Navigate to="/cart" replace />;
  }

  const handleServicePointSelect = () => {
    if (!window.sendcloud || !window.sendcloud.servicePoints) {
      // Le script du sélecteur est chargé en `defer` depuis embed.sendcloud.sc :
      // un blocage réseau ou un bloqueur de publicité le fait disparaître.
      setMessage(t('tunnel.checkout.selecteurIndisponible'));
      return;
    }

    if (!selectedRate?.carrier) {
      setMessage(t('tunnel.checkout.choisirTransporteurDabord'));
      return;
    }

    setMessage(null);

    // Le poids réel du panier, et non un 1,5 kg par défaut : le sélecteur filtre
    // les points relais par capacité, et annoncer un colis trois fois trop lourd
    // écartait des points relais pourtant disponibles.
    const poids = parseFloat(shippingAddress.weight) || 0.5;

    window.sendcloud.servicePoints.open(
      {
        apiKey: import.meta.env.VITE_SENDCLOUD_PUBLIC_KEY,
        country: normaliserPays(shippingAddress.country),
        postalCode: (shippingAddress.postal_code || '').trim(),
        weight: poids,
        carriers: [selectedRate.carrier]
      },
      (servicePoint) => {
        setShippingAddress(prev => ({
          ...prev,
          service_point_id: servicePoint.id,
          service_point_address: servicePoint
        }));
      },
      (error) => {
        // Trois messages d'erreur du sélecteur étaient des alert() en anglais,
        // au milieu d'un tunnel en français ou en néerlandais.
        console.error('Sélecteur de point relais :', error);
        setMessage(t('tunnel.checkout.selecteurErreur'));
      }
    );
  };

  // Valeurs acceptées par le validateur serveur. Tout le reste est ramené à
  // la valeur sûre : mieux vaut une boîte FR par défaut qu'un paiement refusé.
  const langueBoite = (valeur) => (String(valeur || '').toUpperCase() === 'NL' ? 'NL' : 'FR');
  const varianteSure = (valeur) =>
    ['boy', 'girl', 'mixed'].includes(String(valeur || '').toLowerCase())
      ? String(valeur).toLowerCase()
      : undefined;
  const languesStripe = ['fr', 'nl', 'de', 'es', 'en', 'lb'];

  const handleCheckoutRedirect = async () => {
    setIsProcessing(true);
    setMessage(null);

    try {
      const payload = {
        userId: user?.id ?? null, // null = commande invitée, identifiée par l'e-mail
        // `language` et `variant` doivent voyager avec l'article.
        //
        // Ils étaient recopiés dans le panier puis abandonnés ICI : le serveur
        // les attend depuis toujours (métadonnées Stripe, pastille 🇫🇷/🇳🇱 de
        // l'e-mail vendeur, langue du questionnaire PDF) et ne recevait que
        // `undefined`. Un acheteur néerlandophone qui cliquait 🇳🇱 NL payait
        // et recevait une boîte française, sans que personne puisse le savoir.
        // Constaté le 26/08/2026 en capturant la requête réellement émise.
        //
        // `variant` part aussi en clair : le serveur le devinait jusqu'ici en
        // relisant le texte entre parenthèses du nom affiché — donc traduit —
        // et enregistrait « (Jong) » ou « (Mädchen) » selon la langue du
        // navigateur de l'acheteur.
        items: cart.map(item => ({
          name: item.name,
          amount: parseFloat(item.price) || 0,
          quantity: item.quantity || 1,
          weight: item.weight || 0.5,
          id: item.id,
          language: langueBoite(item.language),
          ...(varianteSure(item.variant) ? { variant: varianteSure(item.variant) } : {}),
        })),
        customerEmail: shippingAddress.email,
        customerName: shippingAddress.name,
        // Langue de l'INTERFACE (distincte de la langue de la boîte) : sans
        // elle, Stripe applique locale:'auto' et suit la langue du téléphone.
        // Cas courant à Bruxelles : tunnel choisi en français, page de paiement
        // en néerlandais.
        ...(languesStripe.includes(lang) ? { lang } : {}),
        shippingCost: selectedRate ? parseFloat(selectedRate.price) : 0,
        discount: cartDiscount ? {
          code: cartDiscount.code,
          name: cartDiscount.name,
          type: cartDiscount.type,
          value: cartDiscount.value
        } : undefined,
        shippingData: {
          name: shippingAddress.name,
          email: shippingAddress.email,
          address: shippingAddress.address,
          city: (shippingAddress.city || '').trim(),
          postal_code: (shippingAddress.postal_code || '').trim(),
          // Le serveur n'accepte que le code ISO : on lui envoie la valeur
          // normalisée, jamais la saisie brute.
          country: normaliserPays(shippingAddress.country),
          phone: shippingAddress.phone || '',
          shipping_option_id: selectedRate?.id,
          shipping_weight: shippingAddress.weight, // Use the updated weight from state
          // Deuxième garde, après celle qui vide le point relais au changement
          // de mode : une livraison à domicile ne transporte JAMAIS de point
          // relais, quoi qu'il traîne dans l'état. C'est ce couple d'octets qui
          // décide de l'adresse imprimée sur l'étiquette et de celle facturée
          // par Stripe — il ne doit pas dépendre de l'ordre des clics.
          to_service_point: exigePointRelais(selectedRate)
            ? shippingAddress.service_point_id || null
            : null,
          // Pickup point address for Stripe dashboard display
          pickup_point: exigePointRelais(selectedRate) && shippingAddress.service_point_address ? {
            name: shippingAddress.service_point_address.name || 'Point Relais',
            address: ((shippingAddress.service_point_address.street || '') + ' ' + (shippingAddress.service_point_address.house_number || '')).trim(),
            city: shippingAddress.service_point_address.city || '',
            postal_code: shippingAddress.service_point_address.postal_code || '',
            country: shippingAddress.service_point_address.country || '',
          } : null,
        },
      };

      console.log('Creating checkout session:', payload);
      const data = await createCheckoutSession(payload);

      console.log('Checkout session created:', data);

      if (!data.url) {
        throw new Error('No checkout URL received from backend');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setMessage(t('tunnel.checkout.erreur', { message: error.message }));
      setIsProcessing(false);
    }
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const canContinue = () => {
    if (currentStep === 1) {
      // `estLivrable` et non « pays non vide » : un profil client rempli avant
      // que le menu déroulant n'existe peut contenir « Belgique » en toutes
      // lettres. On laissait alors passer l'étape pour buter à la suivante sur
      // un « pays non desservi » incompréhensible.
      // Le code postal doit faire au moins 4 caractères — exactement le seuil
      // qu'applique la recherche de tarifs. Sans cette borne, l'étape 1 laissait
      // passer « 75 » (faute de frappe, champ tronqué par un remplissage
      // automatique) et l'étape 2 restait figée sur « Calcul des tarifs… »
      // indéfiniment, sans le moindre message. Aucun code postal légitime des
      // 6 pays desservis ne fait moins de 4 caractères.
      return shippingAddress.name && shippingAddress.email && shippingAddress.address &&
        shippingAddress.city && (shippingAddress.postal_code || '').trim().length >= 4 &&
        estLivrable(shippingAddress.country);
    }
    if (currentStep === 2) {
      if (selectedRate === null) return false;
      // Le point relais n'est exigé que par les méthodes qui en ont besoin.
      // Cette condition était inconditionnelle : depuis que TOUTES les options
      // sont proposées, les méthodes à domicile (bpost @home, DPD Home…)
      // apparaissent — et au Portugal c'est même la moins chère. Le bouton
      // « Continuer » restait alors grisé pour toujours.
      return exigePointRelais(selectedRate) ? Boolean(shippingAddress.service_point_id) : true;
    }
    return true;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-surface to-cream py-10 px-5">
      <SEO
        title={t('tunnel.checkout.seoTitre')}
        description={t('tunnel.checkout.seoDescription')}
        url="/checkout"
        keywords="checkout, paiement, livraison puzzle, commande en ligne"
        noindex
      />

      <div className="max-w-6xl mx-auto">
        {/* Progress */}
        <div className="mb-8">
          <GameProgress
            current={currentStep}
            total={3}
            label={t('tunnel.checkout.etape', { n: currentStep })}
            messageRestant={
              currentStep >= 3
                ? t('progression.pretAPayer')
                : 3 - currentStep === 1
                  ? t('progression.etapeRestante', { n: 1 })
                  : t('progression.etapesRestantes', { n: 3 - currentStep })
            }
          />
        </div>

        {/* On le dit explicitement : beaucoup d'acheteurs abandonnent en
            supposant qu'un compte va leur être imposé. */}
        {!user && !paymentSucceeded && (
          <div className="mb-6 rounded-xl border-2 border-[#4AA294]/40 bg-white p-4 text-center">
            <p className="m-0 text-sm font-semibold text-[#17453E]">
              {t('tunnel.checkout.sansCompte')}
            </p>
            <p className="m-0 mt-1 text-xs text-[#33403B]/70">
              {t('tunnel.checkout.dejaCompte')}{' '}
              <Link to="/login" className="font-bold text-[#2F7E72] underline">
                {t('tunnel.checkout.dejaCompteLien')}
              </Link>{' '}
              {t('tunnel.checkout.dejaCompteFin')}
            </p>
          </div>
        )}

        {/* Main Content */}
        {!paymentSucceeded ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Form */}
            <div className="lg:col-span-2 space-y-6">
              {currentStep === 1 && (
                <div className="bg-white rounded-2xl border-2 border-primary/20 p-6 md:p-8 shadow-soft">
                  <h2 className="text-2xl font-bold text-text mb-6">{t('tunnel.checkout.etape1Titre')}</h2>
                  <form className="space-y-4">
                    <div>
                      <input type="text" name="name" className="w-full px-4 py-3 rounded-lg border-2 border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder={t('tunnel.checkout.champNomComplet')} value={shippingAddress.name} onChange={handleAddressChange} required />
                    </div>
                    <div>
                      <input type="email" name="email" className="w-full px-4 py-3 rounded-lg border-2 border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder={t('tunnel.checkout.champEmail')} value={shippingAddress.email} onChange={handleAddressChange} required />
                    </div>
                    <div>
                      <input type="text" name="address" className="w-full px-4 py-3 rounded-lg border-2 border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder={t('tunnel.checkout.champAdresse')} value={shippingAddress.address} onChange={handleAddressChange} required />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input type="text" name="city" className="w-full px-4 py-3 rounded-lg border-2 border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder={t('tunnel.checkout.champVille')} value={shippingAddress.city} onChange={handleAddressChange} required />
                      <input type="text" name="postal_code" className="w-full px-4 py-3 rounded-lg border-2 border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder={t('tunnel.checkout.champCodePostal')} value={shippingAddress.postal_code} onChange={handleAddressChange} required />
                    </div>
                    <div>
                      <select name="country" className="w-full px-4 py-3 rounded-lg border-2 border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white" value={shippingAddress.country} onChange={handleAddressChange} required>
                        <option value="">{t('tunnel.checkout.choisirPays')}</option>
                        {PAYS_LIVRES.map((code) => (
                          <option key={code} value={code}>{t(`tunnel.checkout.pays${code}`)}</option>
                        ))}
                      </select>
                    </div>
                  </form>
                </div>
              )}

              {currentStep === 2 && (
                <div className="bg-white rounded-2xl border-2 border-primary/20 p-6 md:p-8 shadow-soft">
                  <h2 className="text-2xl font-bold text-text mb-6">{t('tunnel.checkout.etape2Titre')}</h2>
                  {/* Les erreurs du sélecteur de points relais sont posées ici,
                      et n'étaient rendues qu'à l'étape 3. Avec un bloqueur de
                      publicité, le clic sur « Choisir un point relais » ne
                      produisait donc rien du tout : pas de carte, pas de
                      message, et « Continuer » grisé pour toujours. */}
                  {message && (
                    <p className="mb-4 rounded-lg bg-[#FDF1EF] px-4 py-3 text-sm font-medium text-[#A33B2A]">
                      {message}
                    </p>
                  )}
                  {/* `rates === null` : rien n'a encore été demandé — le poids
                      du panier ou l'adresse manque. C'est un état d'attente, pas
                      une absence de transporteur. */}
                  {loadingRates || rates === null ? (
                    <div>
                      <p className="m-0 text-text-light italic">{t('tunnel.checkout.calculTarifs')}</p>
                      {repriseEnCours > 0 ? (
                        <p className="m-0 mt-2 text-sm text-text-light">
                          {t('tunnel.checkout.tarifsReprise', {
                            n: repriseEnCours + 1,
                            total: ESSAIS_TARIFS,
                          })}
                        </p>
                      ) : attenteLongue && (
                        <p className="m-0 mt-2 text-sm text-text-light">
                          {t('tunnel.checkout.tarifsAttenteLongue')}
                        </p>
                      )}
                    </div>
                  ) : optionsLivraison.length > 0 ? (
                    <div className="space-y-3">
                      {/* Dit sur TOUS les pays, jamais seulement sur les chers :
                          désigner la destination coûteuse attirerait l'œil sur
                          le montant au lieu de rassurer. */}
                      <p className="m-0 mb-1 text-[13px] text-text-light">
                        {t('tunnel.checkout.portSansMarge')}
                      </p>
                      {optionsVisibles.map((rate, idx) => (
                        <label key={idx} className={`block p-4 rounded-lg border-2 cursor-pointer transition-all ${selectedRate?.id === rate.id ? 'border-primary bg-primary/5' : 'border-primary/20 hover:border-primary/40'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <input
                                type="radio"
                                name="rate"
                                data-cta={`checkout:transporteur-${exigePointRelais(rate) ? 'relais' : 'domicile'}`}
                                checked={selectedRate?.id === rate.id}
                                onChange={() => {
                                  setSelectedRate(rate);
                                  // Le point relais devient caduc dès que le
                                  // transporteur OU le mode de livraison change.
                                  //
                                  // Un relais Mondial Relay n'existe pas dans le
                                  // réseau GLS ou UPS — d'où le test sur le
                                  // transporteur. Mais le mode compte tout autant :
                                  // « bpost @bpack » et « bpost @home » sont le même
                                  // transporteur, et passer de l'un à l'autre gardait
                                  // le point relais. L'écran annonçait alors une
                                  // livraison à domicile pendant que le colis partait
                                  // au relais, facturé au bon prix mais expédié à la
                                  // mauvaise adresse.
                                  const changeDeTransporteur = rate.carrier !== selectedRate?.carrier;
                                  const changeDeMode =
                                    exigePointRelais(rate) !== exigePointRelais(selectedRate);
                                  if (changeDeTransporteur || changeDeMode) {
                                    setShippingAddress(prev => ({
                                      ...prev,
                                      service_point_id: null,
                                      service_point_address: null,
                                    }));
                                  }
                                }}
                                className="w-4 h-4 text-primary"
                              />
                              <span className="font-medium text-text">{rate.name}</span>
                              {/* Le mode de livraison, dit en clair : avec une
                                  dizaine d'options le nom du produit Sendcloud
                                  ne suffit pas à comprendre où arrive le colis. */}
                              <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${exigePointRelais(rate) ? 'bg-primary/15 text-text' : 'bg-green-100 text-green-800'}`}>
                                {exigePointRelais(rate)
                                  ? t('tunnel.checkout.modePointRelais')
                                  : t('tunnel.checkout.modeDomicile')}
                              </span>
                            </div>
                            <div className="text-right">
                              <span className="font-bold text-primary">{parseFloat(rate.price).toFixed(2)}€</span>
                              {/* Le tarif brut barré n'apparaît QUE si une
                                  participation existe réellement : un prix barré
                                  décoratif serait un faux rabais. */}
                              {rate.participation > 0 && (
                                <span className="ml-2 text-xs text-text-light line-through">
                                  {parseFloat(rate.prix_transporteur).toFixed(2)}€
                                </span>
                              )}
                            </div>
                          </div>

                          {rate.participation > 0 && (
                            <p className="m-0 mt-1 text-[12px] font-semibold text-green-800">
                              {t('tunnel.checkout.participation', {
                                montant: parseFloat(rate.participation).toFixed(2).replace('.', ','),
                              })}
                            </p>
                          )}

                          {selectedRate?.id === rate.id && !exigePointRelais(rate) && (
                            <div className="mt-4 pt-4 border-t border-primary/10">
                              <p className="m-0 text-sm text-green-800">
                                {t('tunnel.checkout.livraisonDomicileTexte', {
                                  adresse: `${shippingAddress.address}, ${shippingAddress.postal_code} ${shippingAddress.city}`,
                                })}
                              </p>
                            </div>
                          )}

                          {selectedRate?.id === rate.id && exigePointRelais(rate) && (
                            <div className="mt-4 pt-4 border-t border-primary/10">
                              <div className="mb-3">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-pink/20 rounded-full mb-2">
                                  <span className="text-xs font-bold text-text uppercase tracking-wide">{t('tunnel.checkout.etapeSuivanteRequise')}</span>
                                </div>
                                <p className="text-sm text-text-light">
                                  {t('tunnel.checkout.choisirPointRelaisTexte')}
                                </p>
                              </div>

                              {!shippingAddress.service_point_address ? (
                                <button
                                  type="button"
                                  className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 py-4 font-bold text-lg mt-2"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleServicePointSelect();
                                  }}
                                >
                                  {t('tunnel.checkout.choisirPointRelaisBouton')}
                                </button>
                              ) : (
                                <div className="bg-green-100 border-2 border-green-500 rounded-lg p-4 mt-2">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-bold text-green-800">{t('tunnel.checkout.pointRelaisSelectionne')}</p>
                                      <p className="text-sm text-green-700">{shippingAddress.service_point_address.name}</p>
                                    </div>
                                    <button
                                      type="button"
                                      className="px-4 py-2 bg-green-600 text-white rounded font-medium"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleServicePointSelect();
                                      }}
                                    >
                                      {t('tunnel.checkout.modifier')}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </label>
                      ))}

                      {/* Le reste des transporteurs, à un clic. On annonce le
                          nombre : une liste repliée sans compteur donne à
                          croire qu'il n'y a rien d'autre. */}
                      {optionsLivraison.length > optionsVisibles.length && (
                        <button
                          type="button"
                          onClick={() => setToutesOptions(true)}
                          data-cta="checkout:voir-tous-transporteurs"
                          className="w-full rounded-lg border-2 border-dashed border-primary/30 px-4 py-3 text-sm font-semibold text-text-light transition-all hover:border-primary/60 hover:text-text"
                        >
                          {t('tunnel.checkout.voirPlusOptions', {
                            n: optionsLivraison.length - optionsVisibles.length,
                          })}
                        </button>
                      )}
                      {toutesOptions && optionsLivraison.length > optionsMisesEnAvant.length && (
                        <button
                          type="button"
                          onClick={() => setToutesOptions(false)}
                          className="w-full rounded-lg px-4 py-2 text-sm font-semibold text-text-light underline transition-all hover:text-text"
                        >
                          {t('tunnel.checkout.voirMoinsOptions')}
                        </button>
                      )}
                    </div>
                  ) : erreurTarifs ? (
                    /* L'appel a ÉCHOUÉ — réseau coupé, serveur en erreur,
                       transporteur muet. Ce cas empruntait le message
                       ci-dessous et accusait donc le pays de l'acheteur d'être
                       non desservi, ce qui est faux et le pousse à renoncer.
                       Ici on dit la vérité et on propose de relancer. */
                    <div className="rounded-xl border-2 border-[#D96A5B] bg-[#FDF1EF] p-5">
                      <p className="m-0 mb-2 font-bold text-[#33403B]">
                        {t('tunnel.checkout.tarifsErreurTitre')}
                      </p>
                      <p className="m-0 mb-3 text-sm leading-relaxed text-[#33403B]/80">
                        {t('tunnel.checkout.tarifsErreurTexte')}
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => setTentativeTarifs((n) => n + 1)}
                          data-cta="checkout:tarifs-reessayer"
                          className="rounded-lg bg-[#2F7E72] px-4 py-2 text-sm font-bold text-white transition-all hover:bg-[#17453E]"
                        >
                          {t('tunnel.checkout.tarifsReessayer')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setCurrentStep(1)}
                          className="rounded-lg border-2 border-[#2F7E72] px-4 py-2 text-sm font-bold text-[#2F7E72] transition-all hover:bg-[#4AA294]/10"
                        >
                          {t('tunnel.checkout.indispoModifierAdresse')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Sendcloud n'a renvoyé aucun transporteur pour ce pays :
                       on le dit comme une indisponibilité TEMPORAIRE (demande
                       de l'owner), pas comme une erreur — et on donne deux
                       sorties : changer de pays, ou nous écrire. Cas connu :
                       l'Allemagne depuis le retrait de Mondial Relay. */
                    <div className="rounded-xl border-2 border-[#EDB82F] bg-[#FFF8E6] p-5">
                      <p className="m-0 mb-2 font-bold text-[#33403B]">
                        {t('tunnel.checkout.indispoTitre')}
                      </p>
                      <p className="m-0 mb-3 text-sm leading-relaxed text-[#33403B]/80">
                        {t('tunnel.checkout.indispoTexte')}
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => setCurrentStep(1)}
                          className="rounded-lg border-2 border-[#2F7E72] px-4 py-2 text-sm font-bold text-[#2F7E72] transition-all hover:bg-[#4AA294]/10"
                        >
                          {t('tunnel.checkout.indispoModifierAdresse')}
                        </button>
                        <Link
                          to="/contact"
                          data-cta="checkout:pays-indisponible-contact"
                          className="rounded-lg bg-[#2F7E72] px-4 py-2 text-sm font-bold text-white no-underline transition-all hover:bg-[#17453E]"
                        >
                          {t('tunnel.checkout.indispoContact')}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {currentStep === 3 && (
                <div className="bg-white rounded-2xl border-2 border-primary/20 p-6 md:p-8 shadow-soft">
                  <h2 className="text-2xl font-bold text-text mb-4">{t('tunnel.checkout.etape3Titre')}</h2>
                  <p className="text-text-light mb-6">{t('tunnel.checkout.redirectionStripe')}</p>
                  <GamifiedButton
                    variant="primary"
                    size="lg"
                    data-cta="checkout:payer"
                    onClick={handleCheckoutRedirect}
                    disabled={isProcessing}
                  >
                    {isProcessing ? t('tunnel.checkout.redirection') : t('tunnel.checkout.procederPaiement')}
                  </GamifiedButton>
                  {message && <p className="mt-4 text-red-500 font-medium">{message}</p>}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-between mt-6">
                {/* Les deux boutons effacent le message en changeant d'étape :
                    une erreur de point relais restée affichée à l'étape 3 se
                    lirait comme un échec de paiement. */}
                {currentStep > 1 && !isProcessing && (
                  <GamifiedButton
                    variant="secondary"
                    size="lg"
                    data-cta={`checkout:retour-vers-${currentStep - 1}`}
                    onClick={() => { setMessage(null); setCurrentStep(currentStep - 1); }}
                  >
                    {t('tunnel.checkout.retour')}
                  </GamifiedButton>
                )}
                {currentStep < 3 && (
                  <GamifiedButton
                    variant="primary"
                    size="lg"
                    data-cta={`checkout:etape-${currentStep + 1}`}
                    onClick={() => { setMessage(null); setCurrentStep(currentStep + 1); }}
                    disabled={!canContinue()}
                  >
                    {t('tunnel.checkout.continuer')}
                  </GamifiedButton>
                )}
              </div>
            </div>

            {/* Right: Order Summary */}
            < div className="lg:col-span-1" >
              <div className="bg-white rounded-2xl border-2 border-primary/20 p-6 shadow-soft sticky top-24">
                <h3 className="text-xl font-bold text-text mb-4">{t('tunnel.checkout.recapitulatif')}</h3>
                <div className="space-y-3 mb-4 pb-4 border-b border-primary/20">
                  {cart.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      {/* La langue de la boîte imprimée est un choix payant :
                          elle doit être relisible jusqu'au dernier écran, pas
                          seulement dans le panier. */}
                      <span className="text-text">
                        {item.name}
                        <span className="ml-1 text-text-light">
                          {(item.language || 'FR') === 'NL' ? '🇳🇱 NL' : '🇫🇷 FR'}
                        </span>
                      </span>
                      <span className="font-medium text-text">{(parseFloat(item.price) || 0).toFixed(2)}€</span>
                    </div>
                  ))}
                  {selectedRate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-text">{t('tunnel.checkout.recapLivraison', { nom: selectedRate.name })}</span>
                      <span className="font-medium text-text">{parseFloat(selectedRate.price).toFixed(2)}€</span>
                    </div>
                  )}
                  {cartDiscount && (
                    <div className="flex justify-between text-sm text-green-600 font-medium">
                      <span>{t('tunnel.checkout.recapReduction', { code: cartDiscount.code })}</span>
                      <span>-{(cartDiscount.type === 'percentage'
                        ? (cart.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0) * (cartDiscount.value / 100))
                        : parseFloat(cartDiscount.value)).toFixed(2)}€</span>
                    </div>
                  )}
                </div>
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-text">{t('tunnel.checkout.total')}</span>
                  <span className="text-primary">
                    {(() => {
                      const subtotal = cart.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
                      const shipping = selectedRate ? parseFloat(selectedRate.price) : 0;
                      const discount = cartDiscount
                        ? (cartDiscount.type === 'percentage' ? subtotal * (cartDiscount.value / 100) : parseFloat(cartDiscount.value))
                        : 0;
                      return (subtotal + shipping - discount).toFixed(2);
                    })()}€
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border-2 border-primary/20 p-8 text-center shadow-soft">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-3xl font-bold text-text mb-2">{t('tunnel.checkout.paiementReussi')}</h2>
            <p className="text-text-light">{message}</p>
          </div>
        )}
      </div>
    </div>
  );
}
